import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { coursesApi, Course } from "../lib";
import { showError, showSuccess } from "../lib/toast";
import {
  Plus,
  Search,
  Filter,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  Copy,
  ToggleLeft,
  ToggleRight,
  BookOpen,
  Users,
  Star,
  DollarSign,
  Loader,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  Grid3x3,
  List,
} from "lucide-react";

type FilterStatus = "all" | "published" | "draft";
type ViewMode = "grid" | "list";

export default function InstructorCoursesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all");
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCourses, setTotalCourses] = useState(0);
  const limit = 10;

  useEffect(() => {
    const fetchCourses = async () => {
      if (!user?._id) return;
      
      setLoading(true);
      try {
        const response = await coursesApi.getInstructorCourses(user._id, { 
          page: currentPage, 
          limit,
          isPublished: filterStatus === "published" ? true : filterStatus === "draft" ? false : undefined 
        });
        if (response.success) {
          setCourses(response.data);
          setTotalPages(response.pagination?.pages || 1);
          setTotalCourses(response.pagination?.total || 0);
        }
      } catch (err) {
        showError("Failed to load courses");
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, [user?._id, currentPage, filterStatus]);

  // Search filtering is done client-side
  const filteredCourses = courses.filter((course) => 
    course.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDelete = async (courseId: string) => {
    if (!confirm("Are you sure you want to delete this course? This action cannot be undone.")) {
      return;
    }

    setActionLoading(courseId);
    try {
      const response = await coursesApi.deleteCourse(courseId);
      if (response.success) {
        setCourses(courses.filter(c => c._id !== courseId));
        showSuccess("Course deleted successfully");
      }
    } catch (err: any) {
      showError(err.message || "Failed to delete course");
    } finally {
      setActionLoading(null);
    }
  };

  const handlePublishToggle = async (course: Course) => {
    setActionLoading(course._id);
    try {
      if (course.isPublished) {
        const response = await coursesApi.unpublishCourse(course._id);
        if (response.success) {
          setCourses(courses.map(c => c._id === course._id ? { ...c, isPublished: false } : c));
          showSuccess("Course unpublished");
        }
      } else {
        const response = await coursesApi.publishCourse(course._id);
        if (response.success) {
          setCourses(courses.map(c => c._id === course._id ? { ...c, isPublished: true } : c));
          showSuccess("Course published");
        }
      }
    } catch (err: any) {
      showError(err.message || "Failed to update course status");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDuplicate = (course: Course) => {
    showSuccess("Course duplicated! Redirecting to edit...");
    navigate("/dashboard/instructor/create-course", { 
      state: { duplicateFrom: course } 
    });
  };

  const stats = {
    total: totalCourses,
    published: courses.filter(c => c.isPublished).length,
    drafts: courses.filter(c => !c.isPublished).length,
    totalStudents: courses.reduce((acc, c) => acc + (c.enrolledStudents || 0), 0),
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  useEffect(() => {
    // Reset to page 1 when filter changes
    setCurrentPage(1);
  }, [filterStatus]);

  // Grid View Render
  const renderGridView = () => (
    <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3">
      {filteredCourses.map((course) => (
        <div key={course._id} className="overflow-hidden transition-shadow bg-white border border-gray-200 rounded-xl hover:shadow-md">
          <div className="h-32 bg-gray-100">
            {(course.thumbnail || course.thumbnailUrl) ? (
              <img
                src={course.thumbnail || course.thumbnailUrl}
                alt={course.title}
                className="object-cover w-full h-full"
              />
            ) : (
              <div className="flex items-center justify-center w-full h-full">
                <BookOpen className="w-10 h-10 text-gray-300" />
              </div>
            )}
          </div>
          <div className="p-4">
            <div className="flex items-start justify-between mb-2">
              <h6 className="flex-1 font-medium text-gray-900 truncate">
                {course.title}
              </h6>
              <span
                className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                  course.isPublished
                    ? "bg-green-100 text-green-800"
                    : "bg-yellow-100 text-yellow-800"
                }`}
              >
                {course.isPublished ? "Published" : "Draft"}
              </span>
            </div>
            <p className="mb-3 text-sm text-gray-500 truncate">
              {course.category}
            </p>

            <div className="flex items-center gap-4 mb-3 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                {course.enrolledStudents?.toLocaleString() || 0}
              </span>
              <span className="flex items-center gap-1">
                <Star className="w-3 h-3" />
                {course.rating?.toFixed(1) || 0}
              </span>
              <span className="flex items-center gap-1">
                <DollarSign className="w-3 h-3" />
                {course.price === 0 ? "Free" : `${course.currency || "XAF"} ${course.price.toLocaleString()}`}
              </span>
            </div>

            <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
              <Link
                to={`/course/${course._id}`}
                target="_blank"
                className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 rounded-lg hover:text-gray-900 hover:bg-gray-100"
              >
                <Eye className="w-3 h-3" /> View
              </Link>
              <Link
                to={`/dashboard/instructor/courses/${course._id}/edit`}
                className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 rounded-lg hover:text-gray-900 hover:bg-gray-100"
              >
                <Edit className="w-3 h-3" /> Edit
              </Link>
              <button
                onClick={() => handlePublishToggle(course)}
                disabled={actionLoading === course._id}
                className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 rounded-lg hover:text-gray-900 hover:bg-gray-100 disabled:opacity-50"
                title={course.isPublished ? "Unpublish" : "Publish"}
              >
                {actionLoading === course._id ? (
                  <Loader className="w-3 h-3 animate-spin" />
                ) : course.isPublished ? (
                  <ToggleLeft className="w-3 h-3" />
                ) : (
                  <ToggleRight className="w-3 h-3" />
                )}
                {course.isPublished ? "Unpublish" : "Publish"}
              </button>
              <button
                onClick={() => handleDuplicate(course)}
                className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 rounded-lg hover:text-gray-900 hover:bg-gray-100"
                title="Duplicate"
              >
                <Copy className="w-3 h-3" /> Duplicate
              </button>
              <button
                onClick={() => handleDelete(course._id)}
                disabled={actionLoading === course._id}
                className="flex items-center gap-1 px-2 py-1 text-xs text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50"
                title="Delete"
              >
                {actionLoading === course._id ? (
                  <Loader className="w-3 h-3 animate-spin" />
                ) : (
                  <Trash2 className="w-3 h-3" />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // List View Render
  const renderListView = () => (
    <div className="divide-y divide-gray-200">
      {filteredCourses.map((course) => (
        <div key={course._id} className="p-4 transition-colors hover:bg-gray-50">
          <div className="flex flex-col gap-4 md:flex-row md:items-center">
            {/* Thumbnail */}
            <div className="w-full h-24 overflow-hidden bg-gray-100 rounded-lg md:w-32 md:h-20">
              {(course.thumbnail || course.thumbnailUrl) ? (
                <img
                  src={course.thumbnail || course.thumbnailUrl}
                  alt={course.title}
                  className="object-cover w-full h-full"
                />
              ) : (
                <div className="flex items-center justify-center w-full h-full">
                  <BookOpen className="w-8 h-8 text-gray-300" />
                </div>
              )}
            </div>

            {/* Course Info */}
            <div className="flex-1">
              <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                <div>
                  <h6 className="text-sm font-semibold text-gray-900">
                    {course.title}
                  </h6>
                  <p className="text-xs text-gray-500">{course.category}</p>
                </div>
                <span
                  className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    course.isPublished
                      ? "bg-green-100 text-green-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {course.isPublished ? "Published" : "Draft"}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-4 mb-2 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {course.enrolledStudents?.toLocaleString() || 0} students
                </span>
                <span className="flex items-center gap-1">
                  <Star className="w-3 h-3" />
                  {course.rating?.toFixed(1) || 0} rating
                </span>
                <span className="flex items-center gap-1">
                  <DollarSign className="w-3 h-3" />
                  {course.price === 0 ? "Free" : `${course.currency || "XAF"} ${course.price.toLocaleString()}`}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={`/course/${course._id}`}
                target="_blank"
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 rounded-lg hover:text-gray-900 hover:bg-gray-100"
              >
                <Eye className="w-3.5 h-3.5" /> View
              </Link>
              <Link
                to={`/dashboard/instructor/courses/${course._id}/edit`}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 rounded-lg hover:text-gray-900 hover:bg-gray-100"
              >
                <Edit className="w-3.5 h-3.5" /> Edit
              </Link>
              <button
                onClick={() => handlePublishToggle(course)}
                disabled={actionLoading === course._id}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 rounded-lg hover:text-gray-900 hover:bg-gray-100 disabled:opacity-50"
              >
                {actionLoading === course._id ? (
                  <Loader className="w-3.5 h-3.5 animate-spin" />
                ) : course.isPublished ? (
                  <ToggleLeft className="w-3.5 h-3.5" />
                ) : (
                  <ToggleRight className="w-3.5 h-3.5" />
                )}
                {course.isPublished ? "Unpublish" : "Publish"}
              </button>
              <button
                onClick={() => handleDuplicate(course)}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 rounded-lg hover:text-gray-900 hover:bg-gray-100"
              >
                <Copy className="w-3.5 h-3.5" /> Duplicate
              </button>
              <button
                onClick={() => handleDelete(course._id)}
                disabled={actionLoading === course._id}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50"
              >
                {actionLoading === course._id ? (
                  <Loader className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Trash2 className="w-3.5 h-3.5" />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 mb-6 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-900 md:text-xl">My Courses</h1>
          <p className="text-xs text-gray-500 mt-0.5">Manage and track your course performance</p>
        </div>
        <Link
          to="/dashboard/instructor/create-course"
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white transition-colors rounded-lg bg-primary hover:bg-primary/90 w-fit"
        >
          <Plus className="w-4 h-4" />
          Create Course
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 mb-6 sm:grid-cols-4">
        <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
              <BookOpen className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-xs text-gray-500">Total Courses</p>
            </div>
          </div>
        </div>
        <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg">
              <ToggleRight className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{stats.published}</p>
              <p className="text-xs text-gray-500">Published</p>
            </div>
          </div>
        </div>
        <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-yellow-100 rounded-lg">
              <ToggleLeft className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{stats.drafts}</p>
              <p className="text-xs text-gray-500">Drafts</p>
            </div>
          </div>
        </div>
        <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{stats.totalStudents.toLocaleString()}</p>
              <p className="text-xs text-gray-500">Total Students</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & View Toggle */}
      <div className="p-4 mb-6 bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:flex-1">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute w-4 h-4 text-gray-400 -translate-y-1/2 left-3 top-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search courses..."
                className="w-full py-2 pl-10 pr-4 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="all">All Status</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
            </div>
          </div>

          {/* View Toggle Buttons */}
          <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-lg w-fit">
            <button
              onClick={() => setViewMode("grid")}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                viewMode === "grid"
                  ? "bg-white text-primary shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <Grid3x3 className="w-4 h-4" />
              Grid
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                viewMode === "list"
                  ? "bg-white text-primary shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <List className="w-4 h-4" />
              List
            </button>
          </div>
        </div>
      </div>

      {/* Courses Display */}
      <div className="overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
        {filteredCourses.length > 0 ? (
          viewMode === "grid" ? renderGridView() : renderListView()
        ) : (
          <div className="py-12 text-center">
            <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="mb-2 text-gray-500">No courses found</p>
            <p className="mb-4 text-sm text-gray-400">
              {searchQuery || filterStatus !== "all" 
                ? "Try adjusting your search or filters" 
                : "Create your first course to get started"}
            </p>
            <Link
              to="/dashboard/instructor/create-course"
              className="text-sm text-primary hover:underline"
            >
              Create a new course
            </Link>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col gap-3 p-4 border-t border-gray-100 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-xs text-center text-gray-500 sm:text-left">
              Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, totalCourses)} of {totalCourses} courses
            </div>
            <div className="flex items-center justify-center gap-1">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 text-gray-500 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-3 py-1 text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 text-gray-500 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}